import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useAdminLessons } from '../../hooks/useAdminLessons';
import LessonEditor from './LessonEditor';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit, FiTrash2, FiPlay, FiGripVertical, FiClock } = FiIcons;

const LessonManager = ({ moduleId, lessons }) => {
  const { createLesson, updateLesson, deleteLesson, reorderLessons } = useAdminLessons();
  const [editingLesson, setEditingLesson] = useState(null);
  const [showNewLessonForm, setShowNewLessonForm] = useState(false);

  const handleCreateLesson = async (lessonData) => {
    const orderIndex = lessons.length;
    await createLesson(moduleId, {
      ...lessonData,
      order_index: orderIndex
    });
    setShowNewLessonForm(false);
  };

  const handleUpdateLesson = async (lessonId, lessonData) => {
    await updateLesson(lessonId, lessonData);
    setEditingLesson(null);
  };

  const handleDeleteLesson = async (lessonId) => {
    if (window.confirm('Are you sure you want to delete this lesson?')) {
      await deleteLesson(lessonId);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    reorderLessons(lessons, sourceIndex, destinationIndex);
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-gray-900">Lessons</h4>
        <button
          onClick={() => setShowNewLessonForm(true)}
          className="text-nutrition-600 hover:text-nutrition-700 font-medium flex items-center space-x-1"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Lesson</span>
        </button>
      </div>

      {/* New Lesson Form */}
      <AnimatePresence>
        {showNewLessonForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4"
          >
            <LessonEditor
              onSave={handleCreateLesson}
              onCancel={() => setShowNewLessonForm(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lessons List */}
      {lessons.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <SafeIcon icon={FiPlay} className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">No lessons in this module yet</p>
          <button
            onClick={() => setShowNewLessonForm(true)}
            className="text-nutrition-600 hover:text-nutrition-700 text-sm font-medium mt-2"
          >
            Add your first lesson
          </button>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={`lessons-${moduleId}`}>
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                {lessons.map((lesson, index) => (
                  <Draggable key={lesson.id} draggableId={lesson.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`bg-white border border-gray-200 rounded-lg p-3 ${
                          snapshot.isDragging ? 'shadow-lg' : ''
                        }`}
                      >
                        {editingLesson === lesson.id ? (
                          <LessonEditor
                            lesson={lesson}
                            onSave={(data) => handleUpdateLesson(lesson.id, data)}
                            onCancel={() => setEditingLesson(null)}
                          />
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div {...provided.dragHandleProps} className="cursor-grab">
                                <SafeIcon icon={FiGripVertical} className="w-4 h-4 text-gray-400" />
                              </div>
                              
                              <div className="flex-1">
                                <h5 className="font-medium text-gray-900">
                                  {index + 1}. {lesson.title}
                                </h5>
                                <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                                  {lesson.estimated_minutes > 0 && (
                                    <span className="flex items-center space-x-1">
                                      <SafeIcon icon={FiClock} className="w-3 h-3" />
                                      <span>{lesson.estimated_minutes} min</span>
                                    </span>
                                  )}
                                  {lesson.video_embed_url && (
                                    <span className="flex items-center space-x-1">
                                      <SafeIcon icon={FiPlay} className="w-3 h-3" />
                                      <span>Video</span>
                                    </span>
                                  )}
                                  {lesson.resources && lesson.resources.length > 0 && (
                                    <span>{lesson.resources.length} resources</span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => setEditingLesson(lesson.id)}
                                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                              >
                                <SafeIcon icon={FiEdit} className="w-4 h-4" />
                              </button>
                              
                              <button
                                onClick={() => handleDeleteLesson(lesson.id)}
                                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              >
                                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
};

export default LessonManager;